import { Menu, MenuProps, Popover } from "antd";
import { useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import GlobalButton from "./GlobalButton";
import {
  greyColor,
  maxWidth,
  primaryColor,
  primaryFont,
  retina,
  wMedium,
} from "../../../Utils/utilsStyle";
import DownAngleIcon from "../assets/svgIcons/DownAngleIcon";
import AscendingIcon from "../assets/svgIcons/AscendingIcon";
import DescendingIcon from "../assets/svgIcons/DescendingIcon";
import { IsMobile } from "../../../Utils/UtilityFunctions";
import FilterIcon from '../assets/svgIcons/FilterIcon';
import classNames from 'classnames';

export type SortOrder = "asc" | "desc";

const SortPopupStyle = createGlobalStyle`
  .ant-popover.custom-sort-popup {
  &.ant-popover-placement-bottomLeft {
    padding-top: 0px !important;
  }
  &.ant-popover-placement-bottomRight {
    padding-top: 0px !important;
  }
  .ant-popover-content {
    .ant-popover-arrow {
      display: none;
    }
    .ant-popover-inner {
      border-radius: 8px;
      border: 1px solid #dadada;
      background: #fff;
      box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.15);
      width: fit-content;
      padding: 0px !important;
      .ant-popover-inner-content {
        width: unset !important;
        height: unset !important;
        padding: 0px !important;
        .menu-for-sorting {
          @media all and (max-width: ${maxWidth}), ${retina} {
            width: 150px !important;
          }
          &.ant-menu-vertical {
            border-right: 0px !important;
          }
          padding: 0px !important;
          margin-top: 8px !important;
          .ant-menu-item {
            display: flex;
            align-items: center;
            height: 32px;
            width: 100% !important;
            margin: 0px !important;
            padding-inline: 12px !important;
            border-radius: 0px !important;
            svg {
              min-width: 20px;
            }
            .ant-menu-title-content {
              margin-left: 6px;
              color: ${greyColor};
              font-family: ${primaryFont};
              font-size: 16px;
              font-weight: ${wMedium};
              line-height: 20px;
              letter-spacing: -0.4px;
              @media all and (max-width: ${maxWidth}), ${retina} {
                font-size: 14px;
                line-height: 18px;
              }
            }
            &:hover,
            &.ant-menu-item-active {
              background-color: #f1f1f1 !important;
              svg path {
                fill: ${primaryColor};
              }
              .ant-menu-title-content {
                color: ${primaryColor};
              }
            }
            &.ant-menu-item-selected {
              background-color: transparent !important;
              svg path {
                fill: ${primaryColor};
              }
              .ant-menu-title-content {
                color: ${primaryColor};
              }
            }
          }
        }
      }
    }
  }
}
`;

const StyleWrapper = styled.div`
  .custom-sort-popover {
    button {
      .separator-right {
        margin: 0px 5px 0px 10px;
        background-color: #ddd;
      }
    }
    .custom-sort-button.icon-mode {
      width: 31px;
      height: 28px;
      padding: 0;
      margin: 0;
      justify-content: center;

      svg {
        width: 12px;
        height: 20px;
        min-width: 12px;
        margin: 0;
      }
    }
  }
`;

const ContentWrapper = styled.div`
  .reset-button {
    display: inline-flex;
    justify-content: flex-end;
    width: 100%;
    padding-right: 16px;
    padding-bottom: 10px;
    color: ${primaryColor};
    font-family: ${primaryFont};
    font-size: 14px;
    font-weight: ${wMedium};
    line-height: 20px;
    letter-spacing: -0.4px;
    cursor: pointer;
    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
`;

type Props = {
  value?: SortOrder | null;
  onSortChange?: (order: SortOrder | null) => void;
  disabled?: boolean;
  iconMode?: boolean;
} & typeof defaultProps;

const defaultProps = {
  onSortChange: (_order: SortOrder | null) => { },
  iconMode: false,
};

const CustomSort = (props: Props) => {
  const { value, onSortChange, disabled, iconMode } = props;

  const [open, setOpen] = useState(false);
  const isMob = IsMobile();
  const selectedOrder: SortOrder | null = value ?? null;

  const handleSelect = (order: SortOrder) => {
    setOpen(false);
    onSortChange(order);
  };

  const handleReset = () => {
    if (!selectedOrder) return;
    setOpen(false);
    onSortChange(null);
  };
  type MenuItem = Required<MenuProps>["items"][number];

  function getItem(
    label: React.ReactNode,
    key?: React.Key | null,
    icon?: React.ReactNode,
    type?: "group",
  ): MenuItem {
    return {
      key,
      icon,
      label,
      type,
    } as MenuItem;
  }
  const items: MenuItem[] = [
    getItem("A to Z", "1", <AscendingIcon />),
    getItem("Z to A", "2", <DescendingIcon />),
  ];

  const selectedKeys = selectedOrder === "asc"
    ? ["1"]
    : selectedOrder === "desc"
      ? ["2"]
      : [];

  const onClick: MenuProps["onClick"] = (e) => {
    if (e.key === "1") {
      handleSelect("asc");
    }
    if (e.key === "2") {
      handleSelect("desc");
    }
  };

  const content = (
    <ContentWrapper>
      <Menu
        onClick={onClick}
        style={{ width: isMob ? 150 : 196, borderRadius: 8 }}
        mode="vertical"
        items={items}
        selectedKeys={selectedKeys}
        className="menu-for-sorting"
      />
      <span
        className={classNames("reset-button", { disabled: selectedOrder === null })}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleReset();
          }
        }}
        onClick={handleReset}
        onKeyDown={(e) => {
          if (e.key === " ") {
            e.preventDefault();
          }
        }}
      >
        Reset
      </span>
    </ContentWrapper>
  );

  return (
    <StyleWrapper>
      <SortPopupStyle />
      <Popover
        placement="bottomLeft"
        content={content}
        trigger="click"
        open={disabled ? false : open}
        onOpenChange={(newOpen) => {
          if (!disabled) setOpen(newOpen);
        }}
        getPopupContainer={() => document.body}
        className="custom-sort-popover"
        rootClassName="custom-sort-popup"
        arrow={false}
        zIndex={1}
      >
        <div
          aria-hidden="true"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <GlobalButton
            title={iconMode ? "" : "Sort"}
            outlineColor={open || selectedOrder ? primaryColor : "#DADADA"}
            textColor={open || selectedOrder ? primaryColor : "#7E7E7E;"}
            bgColor={open || selectedOrder ? "#E8F8EC;" : "#fff"}
            innerSpace="9px 12px"
            disabled={disabled}
            buttonClassName={iconMode ? "custom-sort-button icon-mode" : "custom-sort-button"}
            onButtonClick={() => { }}
            RightSeparator={!iconMode}
            height="38px"
            IconRight={iconMode ? undefined :
              <span
                style={{
                  display: "inline-flex",
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                <DownAngleIcon
                  color={
                    open || selectedOrder
                      ? primaryColor
                      : "rgba(126, 126, 126, 1)"
                  }
                />
              </span>
            }
            IconLeft={iconMode ? <FilterIcon /> : undefined}
          />
        </div>
      </Popover>
    </StyleWrapper>
  );
};

CustomSort.defaultProps = defaultProps;

export default CustomSort;
